import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import CookieBanner from '../components/CookieBanner';
import { LEGAL_LINKS } from './LegalPage';
import PhoneChatScreen from '../components/ui/phone-chat';
import AppShowcase from '../components/landing/AppShowcase';
import { StatusBadge } from '../components/ui/status-badge';
import {
  RiShieldCheckLine,
  RiCheckboxCircleFill,
  RiRobot2Line,
  RiDashboardLine,
  RiApps2Line,
  RiBankCardLine,
  RiUserStarLine,
  RiBuilding4Line,
  RiLockPasswordLine,
  RiQuestionLine
} from '@remixicon/react';
import {
  ArrowRight, ArrowUpRight, X, Check, CreditCard, Target, CalendarDays, Gauge, ArrowLeftRight, Receipt,
  ShieldCheck, Smartphone, MonitorSmartphone, Bot, ChevronDown, Building2, User,
  Landmark, ArrowUp, ArrowDown, Send, Lock, KeyRound, Database, LogIn,
  ShoppingCart, Zap, RefreshCw, MessageSquare, Shield, Bell, CheckCircle2,
  BarChart3, PieChart, SlidersHorizontal, Play, LayoutDashboard, Plus, MoreHorizontal, Wifi
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

/* ─────────────────────────────────────────────────────────────────────────────
   MOCKUPS VISUAIS EM CÓDIGO (Alto Padrão Fintech / Identidade Gastify)
───────────────────────────────────────────────────────────────────────────── */

/**
 * Mockup autêntico de Smartphone iPhone 16 Pro com acabamento de titânio escuro,
 * Dynamic Island, botões de hardware físicos, ground shadow e barra PWA 100% centralizada.
 * Dashboard da Sofia com acabamento de altíssima fidelidade.
 */
function PhoneFrame({ id, className = '', tilt = false, decorative = true, children }) {
  return (
    <div
      id={id}
      className={`relative mx-auto w-[280px] sm:w-[305px] select-none transition-transform duration-500 ${
        tilt ? 'transform -rotate-2 hover:rotate-0' : ''
      } ${className}`}
      aria-hidden={decorative ? 'true' : undefined}
    >
      {/* Sombra realista e glow esmeralda de fundo */}
      <div className="absolute -inset-4 bg-gradient-to-tr from-emerald-500/20 via-teal-500/10 to-transparent rounded-[54px] blur-2xl -z-10 pointer-events-none" />

      {/* Botões físicos de hardware nas laterais (estilo iPhone 15/16 Pro) */}
      {/* Esquerda: Botão de Ação + Volume Up + Volume Down */}
      <div className="absolute -left-[3px] top-[74px] w-[3px] h-[15px] bg-[#3a3547] rounded-l-sm shadow-xs z-20" />
      <div className="absolute -left-[3px] top-[102px] w-[3px] h-[34px] bg-[#3a3547] rounded-l-sm shadow-xs z-20" />
      <div className="absolute -left-[3px] top-[144px] w-[3px] h-[34px] bg-[#3a3547] rounded-l-sm shadow-xs z-20" />
      {/* Direita: Botão Power / Side Button */}
      <div className="absolute -right-[3px] top-[110px] w-[3px] h-[48px] bg-[#3a3547] rounded-r-sm shadow-xs z-20" />

      {/* Carcaça externa Titânio Escuro / Deep Purple com chanfro metálico */}
      <div className="relative rounded-[48px] bg-gradient-to-b from-[#2e293a] via-[#1c1826] to-[#252030] p-[7px] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7),0_0_0_1px_rgba(255,255,255,0.18),inset_0_1px_1px_rgba(255,255,255,0.35)]">
        
        {/* Moldura preta interna ultra-fina (Bezel simétrico) */}
        <div className="rounded-[42px] bg-black p-[2.5px] shadow-inner">
          
          {/* Tela ativa do iPhone */}
          <div className="rounded-[40px] bg-[#F8FAFC] overflow-hidden text-slate-800 flex flex-col justify-between">
            
            {/* Status Bar com Dynamic Island e sensores */}
            <div className="relative bg-white pt-2.5 px-4 pb-1.5 flex items-center justify-between text-[10px] font-bold text-slate-900 border-b border-slate-100/80 flex-shrink-0">
              <span className="w-10 pl-0.5 tracking-tight">9:41</span>
              
              {/* Dynamic Island com Câmera TrueDepth e sensor */}
              <div className="w-[82px] h-[22px] bg-black rounded-full flex items-center justify-between px-2 shadow-xs">
                {/* Sensor FaceID sutil */}
                <span className="w-1.5 h-1.5 rounded-full bg-[#111827]" />
                {/* Câmera frontal com reflexo de lente safira */}
                <div className="w-2.5 h-2.5 rounded-full bg-[#0a0f1d] border border-[#1e293b]/80 flex items-center justify-center">
                  <span className="w-1 h-1 rounded-full bg-cyan-400/40" />
                </div>
              </div>

              {/* Conectividade: 5G, Wi-Fi e Bateria estilizada */}
              <div className="w-10 flex items-center justify-end gap-1 text-slate-800">
                <span className="text-[8px] font-black tracking-tighter">5G</span>
                <Wifi size={10} strokeWidth={2.5} className="text-slate-800" />
                <div className="w-4 h-2 rounded-[2px] border border-slate-800 p-[1px] flex items-center relative after:content-[''] after:w-[1px] after:h-[3px] after:bg-slate-800 after:absolute after:-right-[2px] after:rounded-r-[1px]">
                  <div className="h-full w-2 bg-emerald-500 rounded-[0.5px]" />
                </div>
              </div>
            </div>

            {children}
          </div>
        </div>
      </div>

      {/* Sombra de solo tridimensional (Ground shadow realista) */}
      <div className="w-[72%] h-3.5 bg-black/20 blur-md rounded-full mx-auto mt-3 -mb-1" />
    </div>
  );
}

/** Dashboard da Sofia dentro do mockup de celular. */
function PhoneScreenSofia({ className = '', tilt = false }) {
  const bars = [
    { label: 'Abr', val: 45, exp: 26 },
    { label: 'Mai', val: 68, exp: 34 },
    { label: 'Jun', val: 54, exp: 30 },
    { label: 'Jul', val: 86, exp: 42 },
    { label: 'Ago', val: 72, exp: 38 },
    { label: 'Set', val: 96, exp: 48 },
  ];

  return (
    <PhoneFrame id="hero-phone-sofia" className={className} tilt={tilt}>
            {/* Conteúdo do App Gastify */}
            <div className="p-3 space-y-2 flex-1">
              
              {/* Header do Usuário - Sofia */}
              <div className="flex items-center justify-between pt-0.5">
                <div className="flex items-center gap-2">
                  <div className="relative w-8 h-8 rounded-full overflow-hidden ring-2 ring-emerald-500/40 ring-offset-1 flex-shrink-0 shadow-xs">
                    <img
                      src="/images/sofia.jpg"
                      alt="Sofia"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="text-[12px] font-extrabold text-slate-900 leading-tight flex items-center gap-1">
                      Olá, Sofia <span className="text-amber-500">👋</span>
                    </p>
                    <p className="text-[9px] text-slate-400 font-medium leading-none">Vamos para um dia organizado?</p>
                  </div>
                </div>
                <div className="relative w-7 h-7 rounded-full bg-white shadow-xs border border-slate-200/70 flex items-center justify-center text-slate-600">
                  <Bell size={12} />
                  <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-emerald-500 ring-1 ring-white" />
                </div>
              </div>

              {/* 4 Cards de Métricas em Grid 2x2 */}
              <div className="grid grid-cols-2 gap-1.5">
                <div className="bg-white rounded-xl p-2 shadow-xs border border-slate-200/70">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="w-5 h-5 rounded-md bg-emerald-50 text-emerald-600 flex items-center justify-center">
                      <ArrowUp size={11} strokeWidth={2.5} />
                    </span>
                    <span className="text-[8px] font-bold text-emerald-600 bg-emerald-50 px-1 py-0.5 rounded-full">+12%</span>
                  </div>
                  <p className="text-[8px] font-medium text-slate-400">Receitas</p>
                  <p className="text-[12px] font-extrabold text-slate-900">R$ 6.000</p>
                </div>

                <div className="bg-white rounded-xl p-2 shadow-xs border border-slate-200/70">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="w-5 h-5 rounded-md bg-rose-50 text-rose-500 flex items-center justify-center">
                      <ArrowDown size={11} strokeWidth={2.5} />
                    </span>
                    <span className="text-[8px] font-bold text-rose-500 bg-rose-50 px-1 py-0.5 rounded-full">-5%</span>
                  </div>
                  <p className="text-[8px] font-medium text-slate-400">Despesas</p>
                  <p className="text-[12px] font-extrabold text-slate-900">R$ 2.150</p>
                </div>

                <div className="bg-white rounded-xl p-2 shadow-xs border border-slate-200/70">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="w-5 h-5 rounded-md bg-indigo-50 text-indigo-600 flex items-center justify-center">
                      <Landmark size={11} />
                    </span>
                    <span className="w-5 h-2 flex items-center justify-end text-emerald-500">
                      <svg className="w-4 h-1.5" viewBox="0 0 20 8" fill="none"><path d="M1 7L7 4L13 5L19 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                    </span>
                  </div>
                  <p className="text-[8px] font-medium text-slate-400">Saldo atual</p>
                  <p className="text-[12px] font-extrabold text-slate-900">R$ 8.420</p>
                </div>

                <div className="bg-white rounded-xl p-2 shadow-xs border border-slate-200/70">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="w-5 h-5 rounded-md bg-amber-50 text-amber-500 flex items-center justify-center">
                      <CreditCard size={11} />
                    </span>
                    <span className="w-5 h-2 flex items-center justify-end text-emerald-500">
                      <svg className="w-4 h-1.5" viewBox="0 0 20 8" fill="none"><path d="M1 6L6 5L12 2L19 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                    </span>
                  </div>
                  <p className="text-[8px] font-medium text-slate-400">Cartões</p>
                  <p className="text-[12px] font-extrabold text-slate-900">R$ 1.650</p>
                </div>
              </div>

              {/* Gráfico de Barras - Últimos 6 meses */}
              <div className="bg-white rounded-xl p-2.5 shadow-xs border border-slate-200/70">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-[10px] font-bold text-slate-800">Últimos 6 meses</p>
                  <span className="relative z-20 text-[8.5px] font-black text-emerald-800 bg-emerald-100/90 backdrop-blur-md px-2 py-0.5 rounded-full border border-emerald-300/80 shadow-xs flex items-center gap-1">
                    <span className="w-1 h-1 rounded-full bg-emerald-500" />
                    R$ 2.340
                  </span>
                </div>
                <div className="relative z-10 flex items-end justify-between gap-1.5 h-10 px-0.5">
                  {bars.map((b, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-0.5 h-full justify-end">
                      <div className="w-full flex flex-col justify-end gap-0.5 items-center">
                        <div className="w-full rounded-[2.5px] bg-gradient-to-t from-emerald-500 to-emerald-400" style={{ height: `${b.val * 0.19}px` }} />
                        <div className="w-full rounded-[2.5px] bg-gradient-to-t from-rose-500 to-rose-400" style={{ height: `${b.exp * 0.16}px` }} />
                      </div>
                      <span className="text-[7px] text-slate-400 font-semibold leading-none">{b.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Gráfico Donut de Categorias */}
              <div className="bg-white rounded-xl p-2 shadow-xs border border-slate-200/70">
                <p className="text-[10px] font-bold text-slate-800 mb-1">Gastos por categoria</p>
                <div className="flex items-center gap-2">
                  <div className="relative w-[52px] h-[52px] flex-shrink-0 flex items-center justify-center">
                    <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                      <circle cx="18" cy="18" r="14" fill="none" stroke="#E2E8F0" strokeWidth="4" />
                      <circle cx="18" cy="18" r="14" fill="none" stroke="#EF4444" strokeWidth="4" strokeDasharray="32 100" strokeDashoffset="0" />
                      <circle cx="18" cy="18" r="14" fill="none" stroke="#3B82F6" strokeWidth="4" strokeDasharray="28 100" strokeDashoffset="-32" />
                      <circle cx="18" cy="18" r="14" fill="none" stroke="#10B981" strokeWidth="4" strokeDasharray="12 100" strokeDashoffset="-60" />
                      <circle cx="18" cy="18" r="14" fill="none" stroke="#F59E0B" strokeWidth="4" strokeDasharray="10 100" strokeDashoffset="-72" />
                      <circle cx="18" cy="18" r="14" fill="none" stroke="#8B5CF6" strokeWidth="4" strokeDasharray="18 100" strokeDashoffset="-82" />
                    </svg>
                    <div className="absolute text-center leading-none">
                      <p className="text-[8px] font-extrabold text-slate-800">R$ 2.150</p>
                      <p className="text-[6px] text-slate-400 font-medium">Total</p>
                    </div>
                  </div>
                  <div className="flex-1 grid grid-cols-2 gap-x-1.5 gap-y-0.5 text-[8px]">
                    <div className="flex items-center justify-between"><span className="flex items-center gap-1 text-slate-600 truncate"><span className="w-1.5 h-1.5 rounded-full bg-rose-500 flex-shrink-0" />Alimentação</span><span className="font-bold text-slate-800">32%</span></div>
                    <div className="flex items-center justify-between"><span className="flex items-center gap-1 text-slate-600 truncate"><span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />Moradia</span><span className="font-bold text-slate-800">28%</span></div>
                    <div className="flex items-center justify-between"><span className="flex items-center gap-1 text-slate-600 truncate"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />Transporte</span><span className="font-bold text-slate-800">12%</span></div>
                    <div className="flex items-center justify-between"><span className="flex items-center gap-1 text-slate-600 truncate"><span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />Lazer</span><span className="font-bold text-slate-800">10%</span></div>
                  </div>
                </div>
              </div>

              {/* Bottom Navigation PWA - Grid de 5 colunas rigorosamente simétricas com botão '+' flutuante no centro exato */}
              <div className="bg-white rounded-xl py-1 px-1 shadow-xs border border-slate-200/70">
                <div className="grid grid-cols-5 items-center justify-items-center w-full">
                  {/* Coluna 1: Início */}
                  <div className="flex flex-col items-center justify-center text-emerald-600 cursor-pointer">
                    <LayoutDashboard size={13} strokeWidth={2.5} />
                    <span className="text-[7px] font-bold mt-0.5 leading-none">Início</span>
                  </div>

                  {/* Coluna 2: Lançamentos */}
                  <div className="flex flex-col items-center justify-center text-slate-400 cursor-pointer">
                    <Receipt size={13} strokeWidth={2} />
                    <span className="text-[7px] font-medium mt-0.5 leading-none">Lançar</span>
                  </div>

                  {/* Coluna 3: Botão Central Flutuante '+' (Centralizado com precisão matemática no meio) */}
                  <div className="relative flex items-center justify-center -mt-3.5">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-emerald-500 to-teal-400 text-slate-950 font-black flex items-center justify-center shadow-[0_4px_12px_rgba(16,185,129,0.4)] border-2 border-white cursor-pointer active:scale-95 transition-transform">
                      <Plus size={15} strokeWidth={3} className="text-slate-950" />
                    </div>
                  </div>

                  {/* Coluna 4: Cartões */}
                  <div className="flex flex-col items-center justify-center text-slate-400 cursor-pointer">
                    <CreditCard size={13} strokeWidth={2} />
                    <span className="text-[7px] font-medium mt-0.5 leading-none">Cartões</span>
                  </div>

                  {/* Coluna 5: Mais */}
                  <div className="flex flex-col items-center justify-center text-slate-400 cursor-pointer">
                    <MoreHorizontal size={13} strokeWidth={2} />
                    <span className="text-[7px] font-medium mt-0.5 leading-none">Mais</span>
                  </div>
                </div>
              </div>

              {/* Home Indicator do iOS */}
              <div className="pt-0.5 pb-0.5 flex justify-center">
                <span className="w-20 h-1 bg-slate-300 rounded-full" />
              </div>
            </div>
    </PhoneFrame>
  );
}

/**
 * Mockup de Cartões de Crédito em Perspectiva (Inspirado no design Monank)
 */
function CardStackMock() {
  return (
    <div className="relative mx-auto w-full max-w-sm sm:max-w-md h-[270px] sm:h-[300px] flex items-center justify-center select-none" aria-hidden="true">
      {/* Cartão 1: Traseiro (Visa Platinum Teal) */}
      <div className="absolute w-[260px] sm:w-[290px] h-[155px] sm:h-[170px] rounded-3xl bg-gradient-to-br from-teal-800 to-slate-900 p-5 text-white shadow-xl transform -rotate-6 -translate-y-6 -translate-x-3 border border-white/15 opacity-85">
        <div className="flex justify-between items-center text-[11px] font-bold text-teal-200">
          <span>Gastify Platinum</span>
          <span className="text-xs font-black tracking-widest">VISA</span>
        </div>
        <div className="mt-8 text-xs font-mono tracking-widest text-slate-300">•••• •••• •••• 9021</div>
        <div className="mt-4 flex justify-between items-end text-[10px]">
          <div><p className="text-[8px] text-teal-300/70 uppercase">Titular</p><p className="font-bold">SOFIA M.</p></div>
          <div><p className="text-[8px] text-teal-300/70 uppercase">Validade</p><p className="font-bold">09/29</p></div>
        </div>
      </div>

      {/* Cartão 2: Principal / Frontal (Black Emerald Gastify) */}
      <div className="absolute w-[280px] sm:w-[310px] h-[165px] sm:h-[180px] rounded-3xl bg-gradient-to-br from-[#07241C] via-[#0A3328] to-[#041611] p-5 text-white shadow-2xl transform rotate-3 translate-y-4 translate-x-2 border border-emerald-500/30">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs font-black tracking-wide text-emerald-300">Gastify Black</span>
          </div>
          <div className="flex -space-x-2">
            <span className="w-6 h-6 rounded-full bg-rose-500/80" />
            <span className="w-6 h-6 rounded-full bg-amber-500/80" />
          </div>
        </div>
        <div className="mt-7 flex justify-between items-center">
          <span className="text-sm font-mono tracking-widest text-emerald-100">•••• •••• •••• 4589</span>
          <span className="text-[10px] bg-emerald-500/20 text-emerald-300 font-bold px-2 py-0.5 rounded-full border border-emerald-500/30">
            Fatura aberta
          </span>
        </div>
        <div className="mt-4 flex justify-between items-end text-[10px]">
          <div>
            <p className="text-[8px] text-emerald-300/70 uppercase">Limite Disponível</p>
            <p className="font-extrabold text-emerald-400 text-xs">R$ 18.350,00</p>
          </div>
          <div className="text-right">
            <p className="text-[8px] text-emerald-300/70 uppercase">Melhor compra</p>
            <p className="font-bold text-white text-xs">Dia 15</p>
          </div>
        </div>
      </div>

      {/* Badge Flutuante de Notificação */}
      <div className="absolute -bottom-2 -left-2 bg-white/95 backdrop-blur-md rounded-2xl p-3 shadow-xl border border-slate-100 flex items-center gap-2.5 z-20">
        <span className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold">
          <Check size={16} strokeWidth={3} />
        </span>
        <div>
          <p className="text-[11px] font-extrabold text-slate-900">Fatura paga em dia</p>
          <p className="text-[9px] text-slate-500">Nubank · R$ 1.650,00</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Mockup do Chat com IA (Inspirado no Applook/Monank)
 */
function ChatMockRefined() {
  return (
    <div className="w-full max-w-lg mx-auto rounded-[32px] bg-white border border-slate-200/80 p-5 sm:p-6 space-y-4 shadow-xl" aria-hidden="true">
      {/* Cabeçalho do Chat */}
      <div className="flex items-center justify-between pb-3.5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="relative w-10 h-10 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center shadow-xs">
            <Bot size={20} />
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white" />
          </div>
          <div>
            <p className="text-sm font-extrabold text-slate-900 leading-tight">Assistente IA Gastify</p>
            <p className="text-[11px] text-emerald-600 font-medium">Online e conectado</p>
          </div>
        </div>
        <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200/60">
          Inteligência Financeira
        </span>
      </div>

      {/* Mensagem 1 - Usuário */}
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-slate-900 text-white text-xs sm:text-sm px-4 py-2.5 font-medium shadow-xs">
          Adicione R$ 45 de mercado hoje
        </div>
      </div>

      {/* Resposta 1 - IA com card estruturado */}
      <div className="flex justify-start">
        <div className="max-w-[92%] rounded-2xl rounded-bl-sm bg-emerald-50/80 border border-emerald-100 text-slate-800 text-xs sm:text-sm p-3.5 space-y-2">
          <p className="font-semibold text-emerald-800 flex items-center gap-1.5 text-xs">
            <CheckCircle2 size={14} className="text-emerald-600" /> Lançamento realizado com sucesso:
          </p>
          <div className="flex items-center justify-between bg-white rounded-xl p-2.5 border border-emerald-200/60 shadow-2xs">
            <div className="flex items-center gap-2.5">
              <span className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <ShoppingCart size={15} />
              </span>
              <div>
                <p className="font-bold text-slate-900 text-xs">Mercado</p>
                <p className="text-[10px] text-slate-500">Alimentação · hoje</p>
              </div>
            </div>
            <span className="font-extrabold text-rose-500 text-xs">- R$ 45,00</span>
          </div>
        </div>
      </div>

      {/* Mensagem 2 - Usuário */}
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-slate-900 text-white text-xs sm:text-sm px-4 py-2.5 font-medium shadow-xs">
          Quanto gastei com transporte esse mês?
        </div>
      </div>

      {/* Resposta 2 - IA com insight */}
      <div className="flex justify-start">
        <div className="max-w-[92%] rounded-2xl rounded-bl-sm bg-slate-100 border border-slate-200 text-slate-700 text-xs sm:text-sm p-3.5">
          <p className="leading-relaxed">
            Você gastou <strong className="text-emerald-700 font-extrabold">R$ 320,50</strong> em transporte neste mês. Isso representa <strong className="text-slate-900">12%</strong> dos seus gastos totais, 8% abaixo da sua meta.
          </p>
        </div>
      </div>

      {/* Input */}
      <div className="flex items-center gap-2 rounded-2xl bg-slate-50 border border-slate-200 px-3.5 py-2 text-xs sm:text-sm text-slate-500">
        <span className="flex-1 text-slate-400">Escreva um comando ou áudio…</span>
        <button type="button" className="w-7 h-7 rounded-xl bg-emerald-600 text-white flex items-center justify-center hover:bg-emerald-700 transition-colors">
          <Send size={13} />
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   MODAL DE ACESSO
───────────────────────────────────────────────────────────────────────────── */

function AccessModal({ open, onClose }) {
  const navigate = useNavigate();
  const [slug, setSlug] = useState('');
  const [error, setError] = useState('');
  if (!open) return null;

  const submit = (e) => {
    e.preventDefault();
    const s = slug.trim().toLowerCase().replace(/^https?:\/\/[^/]+\//, '').replace(/\/.*$/, '');
    if (!/^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/.test(s)) {
      setError('Digite o nome do seu painel, por exemplo: minha-empresa');
      return;
    }
    navigate(`/${s}/login`);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white rounded-3xl p-6 sm:p-8 shadow-2xl space-y-4 border border-slate-100" data-testid="access-modal">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full mb-2">
              <LogIn size={13} /> Acesso rápido
            </div>
            <h3 className="text-xl font-extrabold text-slate-900">Acessar meu painel</h3>
            <p className="text-sm text-slate-500 mt-1">Digite o nome do seu painel, o mesmo que vem no endereço que você recebeu.</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition" aria-label="Fechar">
            <X size={20} />
          </button>
        </div>
        <div>
          <div className="flex items-center rounded-2xl border border-slate-200 bg-slate-50 focus-within:ring-2 focus-within:ring-emerald-500/30 focus-within:border-emerald-500 overflow-hidden transition">
            <span className="pl-4 text-sm font-semibold text-slate-400 whitespace-nowrap">gastify.com.br/</span>
            <input
              autoFocus value={slug} onChange={(e) => { setSlug(e.target.value); setError(''); }}
              placeholder="minha-empresa" className="flex-1 bg-transparent px-2 py-3.5 text-sm font-medium text-slate-800 outline-none" data-testid="access-slug"
            />
          </div>
          {error && <p className="text-xs font-semibold text-rose-500 mt-2">{error}</p>}
        </div>
        <button type="submit" className="w-full rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold py-3.5 text-sm flex items-center justify-center gap-2 transition-all shadow-md shadow-emerald-600/20 cursor-pointer" data-testid="access-submit">
          Entrar no painel <ArrowRight size={16} />
        </button>
        <p className="text-xs text-slate-400 text-center">
          Ainda não tem acesso?{' '}
          <button type="button" className="font-bold text-emerald-600 hover:underline cursor-pointer" onClick={() => { onClose(); go('contato'); }}>
            Peça o seu
          </button>
        </p>
      </form>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   FORMULÁRIO DE CONTATO (LEAD)
───────────────────────────────────────────────────────────────────────────── */

const inputCls = 'w-full rounded-2xl border border-slate-200 bg-white px-4 py-3.5 text-sm text-slate-800 placeholder-slate-400 outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition shadow-2xs';

function maskPhone(v) {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

function LeadForm() {
  const [form, setForm] = useState({ nome: '', email: '', whatsapp: '', tipo: 'PF', empresa: '', mensagem: '', consentimento: false, website: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.consentimento) { setError('Marque a caixa para autorizar o nosso contato.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/public/leads`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      if (!res.ok) {
        const d = (await res.json().catch(() => ({}))).detail;
        throw new Error(typeof d === 'string' ? d : Array.isArray(d) ? d.map((i) => i.msg).join(' — ') : 'Não foi possível enviar agora. Tente novamente.');
      }
      setDone(true);
    } catch (err) {
      setError(err.message || 'Não foi possível enviar agora. Tente novamente.');
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="rounded-[32px] bg-white p-8 sm:p-10 text-center shadow-xl border border-slate-100" data-testid="lead-sucesso">
        <span className="w-16 h-16 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4 shadow-xs">
          <Check size={32} strokeWidth={2.5} />
        </span>
        <h3 className="text-2xl font-extrabold text-slate-900">Recebemos o seu pedido!</h3>
        <p className="text-sm text-slate-500 mt-2 max-w-sm mx-auto leading-relaxed">
          Em breve entraremos em contato pelo WhatsApp ou pelo e-mail que você informou para liberar o seu acesso.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-[32px] bg-white p-6 sm:p-8 shadow-xl border border-slate-100 space-y-4 text-left" data-testid="lead-form">
      {/* Toggle PF / PJ */}
      <div className="grid grid-cols-2 gap-2 p-1.5 rounded-2xl bg-slate-100 border border-slate-200/50">
        {[['PF', 'Para mim', User], ['PJ', 'Para minha empresa', Building2]].map(([id, label, Icon]) => (
          <button
            key={id} type="button" onClick={() => set('tipo', id)} data-testid={`lead-tipo-${id}`}
            className={`flex items-center justify-center gap-2 rounded-xl py-2.5 text-xs sm:text-sm font-bold transition-all cursor-pointer ${
              form.tipo === id ? 'bg-white text-emerald-700 shadow-xs' : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            <Icon size={15} />{label}
          </button>
        ))}
      </div>

      <input className={inputCls} placeholder="Seu nome" value={form.nome} onChange={(e) => set('nome', e.target.value)} required minLength={2} maxLength={120} data-testid="lead-nome" />
      
      {form.tipo === 'PJ' && (
        <input className={inputCls} placeholder="Nome da empresa" value={form.empresa} onChange={(e) => set('empresa', e.target.value)} maxLength={160} data-testid="lead-empresa" />
      )}

      <div className="grid sm:grid-cols-2 gap-3.5">
        <input type="email" className={inputCls} placeholder="Seu e-mail" value={form.email} onChange={(e) => set('email', e.target.value)} required data-testid="lead-email" />
        <input className={inputCls} placeholder="WhatsApp com DDD" inputMode="tel" value={form.whatsapp} onChange={(e) => set('whatsapp', maskPhone(e.target.value))} required data-testid="lead-whatsapp" />
      </div>

      <textarea className={`${inputCls} resize-none`} rows={3} placeholder="Quer contar um pouco sobre o que precisa? (opcional)" value={form.mensagem} onChange={(e) => set('mensagem', e.target.value)} maxLength={500} />

      {/* Campo invisível anti-bot */}
      <div className="absolute -left-[9999px] w-px h-px overflow-hidden" aria-hidden="true">
        <input tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="Não preencha" />
      </div>

      <label className="flex items-start gap-3 text-xs text-slate-500 cursor-pointer select-none">
        <input type="checkbox" checked={form.consentimento} onChange={(e) => set('consentimento', e.target.checked)} className="mt-0.5 w-4 h-4 accent-emerald-600 rounded" data-testid="lead-consent" />
        <span className="leading-relaxed">Autorizo o Gastify a entrar em contato por WhatsApp e e-mail sobre este pedido. Uso seus dados só para isso.</span>
      </label>

      {error && <p className="text-sm font-medium text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-4 py-2.5" data-testid="lead-erro">{error}</p>}

      <button
        type="submit" disabled={loading}
        className="w-full rounded-2xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-extrabold py-4 text-sm flex items-center justify-center gap-2 transition-all shadow-md shadow-emerald-600/20 cursor-pointer"
        data-testid="lead-enviar"
      >
        {loading ? 'Enviando…' : 'Quero usar o Gastify'} {!loading && <ArrowRight size={17} />}
      </button>
    </form>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   DADOS DAS SEÇÕES
───────────────────────────────────────────────────────────────────────────── */

const FEATURES = [
  { icon: CreditCard, color: 'bg-emerald-50 text-emerald-700 border-emerald-100', title: 'Cartões e faturas', text: 'Lance, feche e acompanhe seus cartões de crédito e débito. Veja faturas e vencimentos em um só lugar.' },
  { icon: CalendarDays, color: 'bg-teal-50 text-teal-700 border-teal-100', title: 'Calendário financeiro', text: 'Visualize todos os seus compromissos financeiros de forma simples e organizada.' },
  { icon: Receipt, color: 'bg-emerald-50 text-emerald-700 border-emerald-100', title: 'Contas a pagar e receber', text: 'Gerencie suas contas, defina lembretes e nunca mais perca um vencimento.' },
  { icon: Target, color: 'bg-teal-50 text-teal-700 border-teal-100', title: 'Metas', text: 'Defina objetivos, acompanhe seu progresso e mantenha o foco no que realmente importa.' },
  { icon: Gauge, color: 'bg-emerald-50 text-emerald-700 border-emerald-100', title: 'Meu Desempenho', text: 'Veja sua evolução financeira com gráficos claros e insights que realmente ajudam.' },
  { icon: ArrowLeftRight, color: 'bg-teal-50 text-teal-700 border-teal-100', title: 'Importar e exportar', text: 'Traga seus dados via CSV e exporte relatórios completos em PDF e Excel quando quiser.' },
];

const FAQ = [
  ['Como eu começo a usar?', 'Preencha o formulário desta página. Entramos em contato para entender o que você precisa e liberamos o seu acesso. Depois é só entrar pelo endereço do seu painel.'],
  ['Serve para pessoa física e para empresa?', 'Sim. Quem usa para a vida pessoal vê só o essencial: saldo, receitas, despesas e cartões. Para empresas dá para ligar indicadores como faturamento, lucro e margem. Cada pessoa escolhe o que aparece.'],
  ['Preciso instalar algum aplicativo?', 'Não. O Gastify funciona direto no navegador, no computador e no celular, e pode ser instalado na tela inicial do aparelho.'],
  ['Meus dados ficam separados dos de outras pessoas?', 'Sim. Cada cliente tem o seu próprio espaço de dados, isolado dos demais.'],
  ['Como o assistente de IA funciona?', 'Você escreve do jeito que fala, como "gastei 150 no mercado", e o assistente lança para você. Dá para pedir resumos e analisar seus gastos também.'],
];

/* ─────────────────────────────────────────────────────────────────────────────
   PÁGINA PRINCIPAL
───────────────────────────────────────────────────────────────────────────── */

export default function HomeLanding() {
  const [access, setAccess] = useState(false);
  const [faq, setFaq] = useState(0);
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' }), 150);
  }, [hash]);

  useEffect(() => {
    const root = document.documentElement;
    const had = root.classList.contains('dark');
    const force = () => { if (root.classList.contains('dark')) root.classList.remove('dark'); };
    force();
    const obs = new MutationObserver(force);
    obs.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => { obs.disconnect(); if (had) root.classList.add('dark'); };
  }, []);

  const nav = [['Recursos', 'recursos'], ['Para quem', 'para-quem'], ['Segurança', 'seguranca'], ['Perguntas', 'perguntas']];

  return (
    <div className="bg-[#FAFBF9] text-slate-800 antialiased selection:bg-emerald-500 selection:text-white" style={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" }} data-testid="landing">
      
      {/* ────────────────── TOP BAR / HEADER ────────────────── */}
      <div style={{ height: 'calc(5rem + env(safe-area-inset-top, 0px))' }} />
      <header className="fixed top-0 inset-x-0 z-50 bg-white/90 backdrop-blur-md border-b border-emerald-950/5" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-4">
          {/* Logotipo Oficial do Gastify */}
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-2.5 cursor-pointer">
            <img src="/gastify-icon-192.png" alt="Gastify" className="w-9 h-9 rounded-xl shadow-xs" />
            <span className="text-xl font-black text-[#0B2720] tracking-tight">Gastify</span>
          </button>

          {/* Navegação Estilo Monank */}
          <nav className="hidden md:flex items-center gap-8">
            {nav.map(([l, id]) => (
              <button key={id} onClick={() => go(id)} className="text-sm font-semibold text-slate-600 hover:text-emerald-700 transition-colors cursor-pointer">
                {l}
              </button>
            ))}
          </nav>

          {/* Botões do Topo */}
          <div className="flex items-center gap-3">
            <button onClick={() => go('contato')} className="hidden sm:inline-flex text-sm font-bold text-slate-700 hover:text-emerald-700 transition-colors cursor-pointer px-3 py-2">
              Quero usar
            </button>
            <button
              onClick={() => setAccess(true)}
              className="inline-flex items-center gap-2 rounded-full bg-[#0B2720] hover:bg-emerald-800 text-white text-sm font-extrabold px-5 py-2.5 transition-all shadow-sm cursor-pointer"
              data-testid="btn-acessar"
            >
              <LogIn size={15} /> Acessar painel
            </button>
          </div>
        </div>
      </header>

      {/* ────────────────── HERO BANNER (Inspirado no Modelo Monank Bold) ────────────────── */}
      <section className="relative overflow-hidden pt-8 pb-16 sm:pb-24 text-center">
        {/* Glow de fundo suave */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[350px] bg-emerald-100/50 rounded-full blur-[120px] pointer-events-none -z-10" />

        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          {/* Badge de Destaque do Sistema & Segurança (Oculto no mobile conforme solicitado) */}
          <StatusBadge
            className="hidden sm:inline-flex mb-6 bg-white shadow-2xs border-emerald-200/80"
            status="success"
            leftIcon={RiShieldCheckLine}
            leftLabel="Dados protegidos"
            rightLabel="Gestão financeira consciente com Inteligência Artificial nativa"
          />

          {/* Super Headline Bold em Tipografia Marcante */}
          <h1 className="text-4xl sm:text-6xl lg:text-[68px] font-black text-[#0B2720] tracking-tight leading-[1.05]">
            Cada gasto,<br />
            uma <span className="text-emerald-600 underline decoration-emerald-300 decoration-wavy decoration-2">decisão consciente.</span>
          </h1>

          {/* Subtítulo elegante */}
          <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Reúna contas, cartões, receitas e despesas em um só lugar. Lance falando com o assistente de IA e acompanhe sua evolução no computador ou no celular.
          </p>

          {/* Botões de Ação estilo Pill Monank */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3.5">
            <button
              onClick={() => go('contato')}
              className="inline-flex items-center gap-2 rounded-full bg-[#0B2720] hover:bg-emerald-800 text-white font-black px-8 py-4 text-sm transition-all shadow-md cursor-pointer"
              data-testid="hero-cta"
            >
              Quero usar o Gastify <ArrowUpRight size={17} strokeWidth={2.5} />
            </button>
            <button
              onClick={() => go('recursos')}
              className="rounded-full bg-white border border-slate-200 text-slate-700 font-extrabold px-7 py-4 text-sm hover:border-emerald-300 hover:text-emerald-700 transition-all cursor-pointer shadow-2xs"
            >
              Ver recursos
            </button>
          </div>

          {/* Mockup Central do Smartphone em Proporção Real da Sofia com Cards Flutuantes */}
          <div className="relative mt-14 sm:mt-18 flex justify-center items-center">
            {/* Card Flutuante Esquerdo: Lançar com IA */}
            <div className="hidden lg:flex absolute left-1/2 -translate-x-[calc(100%+200px)] top-1/4 z-20 flex-col items-start animate-pulse duration-1000">
              <div className="rounded-2xl bg-white border border-emerald-100 p-4 shadow-xl text-left max-w-[290px]">
                <StatusBadge
                  status="success"
                  leftIcon={RiRobot2Line}
                  leftLabel="Assistente IA"
                  rightLabel="Por comando"
                  className="mb-2 bg-emerald-50/80 border-emerald-200 shadow-2xs whitespace-nowrap text-[11px] [&_span]:whitespace-nowrap"
                />
                <p className="text-[12px] font-bold text-slate-800 leading-snug">
                  “Chat, adicione R$ 45 de mercado hoje”
                </p>
              </div>
            </div>

            {/* Smartphone Pro Sofia (19.5:9 Realista) */}
            <PhoneScreenSofia tilt={true} />

            {/* Card Flutuante Direito: Mercado R$ 45,00 Confirmado */}
            <div className="hidden lg:flex absolute right-6 sm:right-12 bottom-1/4 z-20 items-center gap-3 rounded-2xl bg-white border border-emerald-100 px-4 py-3 shadow-xl">
              <span className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold">
                <ShoppingCart size={17} />
              </span>
              <div className="text-left">
                <p className="text-[12px] font-extrabold text-slate-900 leading-tight">Mercado</p>
                <p className="text-[11px] text-emerald-600 font-black">R$ 45,00</p>
              </div>
              <span className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center ml-1">
                <Check size={14} strokeWidth={3} />
              </span>
            </div>
          </div>

          {/* Faixa de Confiança / Logos & Selos Estilo Monank */}
          <div className="mt-16 sm:mt-20 rounded-3xl bg-[#E8F5EF] border border-emerald-100/80 p-5 sm:p-6">
            <p className="text-[11px] font-black tracking-wider uppercase text-emerald-800 mb-3">
              Segurança e privacidade em primeiro lugar
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10 text-xs font-extrabold text-emerald-900/80">
              <span className="flex items-center gap-2"><Shield size={16} className="text-emerald-600" /> Conexão protegida (HTTPS)</span>
              <span className="flex items-center gap-2"><Database size={16} className="text-emerald-600" /> Dados separados por cliente</span>
              <span className="flex items-center gap-2"><RiRobot2Line className="size-4 text-emerald-600" /> IA Financeira Nativa</span>
              <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-600" /> Pensado para a LGPD</span>
            </div>
          </div>

          {/* Diferenciais Reais e Pilares do Sistema Gastify */}
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 text-center">
            <div className="p-4 sm:p-5 rounded-2xl bg-white border border-slate-100 shadow-xs hover:border-emerald-200 transition-colors">
              <p className="text-3xl sm:text-4xl font-black text-[#0B2720]">Seus</p>
              <p className="text-xs sm:text-sm text-slate-800 font-extrabold mt-1">Dados separados</p>
              <p className="text-[11px] text-slate-500 font-medium mt-1">Cada cliente tem o seu próprio espaço de dados.</p>
            </div>
            <div className="p-4 sm:p-5 rounded-2xl bg-white border border-slate-100 shadow-xs hover:border-emerald-200 transition-colors">
              <p className="text-3xl sm:text-4xl font-black text-[#0B2720]">IA</p>
              <p className="text-xs sm:text-sm text-slate-800 font-extrabold mt-1">Assistente nativo</p>
              <p className="text-[11px] text-slate-500 font-medium mt-1">Lance receitas e despesas conversando pelo chat</p>
            </div>
            <div className="p-4 sm:p-5 rounded-2xl bg-white border border-slate-100 shadow-xs hover:border-emerald-200 transition-colors">
              <p className="text-3xl sm:text-4xl font-black text-[#0B2720]">24/7</p>
              <p className="text-xs sm:text-sm text-slate-800 font-extrabold mt-1">Sincronia na nuvem</p>
              <p className="text-[11px] text-slate-500 font-medium mt-1">Acesse de qualquer lugar via PC ou celular</p>
            </div>
            <div className="p-4 sm:p-5 rounded-2xl bg-white border border-slate-100 shadow-xs hover:border-emerald-200 transition-colors">
              <p className="text-3xl sm:text-4xl font-black text-[#0B2720]">Zero</p>
              <p className="text-xs sm:text-sm text-slate-800 font-extrabold mt-1">Planilhas manuais</p>
              <p className="text-[11px] text-slate-500 font-medium mt-1">Centralize contas, faturas, cartões e relatórios automáticos</p>
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────── DOBRA 2: RECURSOS & CONTROLE (Inspirada no Monank) ────────────────── */}
      <section id="recursos" className="py-16 sm:py-24 bg-white border-t border-slate-100 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Lado Esquerdo: Mensagem e Lista de Valores */}
            <div className="space-y-6">
              <StatusBadge
                status="success"
                leftIcon={RiDashboardLine}
                leftLabel="Visão Unificada"
                rightLabel="Tudo em um só lugar"
                className="bg-emerald-50/70 border-emerald-200/80"
              />

              <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-[#0B2720] leading-tight">
                Controle suas finanças de forma <span className="text-emerald-600">simples e completa</span>
              </h2>

              <p className="text-slate-600 leading-relaxed text-base">
                O Gastify reúne suas contas, cartões, receitas e despesas em um só lugar, com uma interface moderna e fácil de usar. Mais organização, mais controle e melhores decisões para o seu dinheiro.
              </p>

              {/* Destaques em Caixas Limpas */}
              <div className="space-y-4 pt-2">
                {[
                  [BarChart3, 'bg-emerald-50 text-emerald-700 border-emerald-100', 'Visualize sua vida financeira em tempo real', 'Saiba exatamente para onde seu dinheiro vai sem perder tempo com cálculos manuais.'],
                  [CreditCard, 'bg-teal-50 text-teal-700 border-teal-100', 'Organize suas contas e cartões', 'Conecte suas contas, acompanhe faturas, limites e datas de vencimento com facilidade.'],
                  [Target, 'bg-emerald-50 text-emerald-700 border-emerald-100', 'Planeje seus objetivos', 'Defina metas e acompanhe seu progresso mês a mês com relatórios visuais.'],
                ].map(([Icon, colorCls, title, desc]) => (
                  <div key={title} className="flex items-start gap-4">
                    <span className={`w-11 h-11 rounded-2xl ${colorCls} border flex items-center justify-center flex-shrink-0 mt-0.5 shadow-2xs`}>
                      <Icon size={19} />
                    </span>
                    <div>
                      <p className="font-extrabold text-slate-900 text-sm">{title}</p>
                      <p className="text-xs sm:text-sm text-slate-500 mt-0.5 leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-2">
                <button
                  onClick={() => go('contato')}
                  className="inline-flex items-center gap-2 rounded-full bg-[#0B2720] hover:bg-emerald-800 text-white font-extrabold px-7 py-3.5 text-sm transition-all shadow-sm cursor-pointer"
                >
                  Quero usar o Gastify <ArrowRight size={16} />
                </button>
              </div>
            </div>

            {/* Lado Direito: Mockup do Smartphone Realista da Sofia em Destaque */}
            <div className="relative flex justify-center items-center py-4">
              <PhoneScreenSofia tilt={false} />
            </div>
          </div>

          {/* Grid dos 6 Recursos Fundamentais */}
          <div className="mt-20">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <StatusBadge
                status="success"
                leftIcon={RiApps2Line}
                leftLabel="Recursos Essenciais"
                rightLabel="Controle Diário Completo"
                className="mb-3 bg-emerald-50/70 border-emerald-200/80"
              />
              <h3 className="text-2xl sm:text-3xl font-black text-[#0B2720]">
                Funcionalidades completas para o seu dia a dia
              </h3>
              <p className="text-sm text-slate-500 mt-2">
                Ferramentas simples e poderosas para organizar sua vida financeira, seja pessoal ou para o seu negócio.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {FEATURES.map(({ icon: Icon, color, title, text }) => (
                <div
                  key={title}
                  className="rounded-3xl bg-[#FAFBF9] p-6 sm:p-7 border border-slate-200/70 hover:border-emerald-300 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                >
                  <span className={`w-12 h-12 rounded-2xl ${color} border flex items-center justify-center mb-5 shadow-2xs`}>
                    <Icon size={22} />
                  </span>
                  <p className="text-base font-extrabold text-[#0B2720]">{title}</p>
                  <p className="text-xs sm:text-sm text-slate-500 mt-2 leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────── DOBRA 3: CARTÕES & FATURAS (Inspirada no Monank Cartões) ────────────────── */}
      <section className="py-16 sm:py-24 bg-[#F2F7F4] border-t border-slate-200/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Lado Esquerdo: Mockup da Pilha de Cartões em Perspectiva */}
            <div className="order-2 lg:order-1 flex justify-center">
              <CardStackMock />
            </div>

            {/* Lado Direito: Headline e Recursos de Cartão */}
            <div className="order-1 lg:order-2 space-y-6">
              <StatusBadge
                status="success"
                leftIcon={RiBankCardLine}
                leftLabel="Gestão de Cartões"
                rightLabel="Limites & Faturas Organizadas"
                className="bg-emerald-100/70 border-emerald-300/80"
              />

              <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-[#0B2720] leading-tight">
                Simplifique sua carteira, gerencie seus cartões sem esforço.
              </h2>

              <p className="text-slate-600 leading-relaxed text-base">
                O Gastify permite organizar todos os seus cartões de crédito e débito em um só lugar. Acompanhe limites disponíveis, faturas em aberto e saiba exatamente qual é o melhor cartão para fazer compras hoje.
              </p>

              <ul className="space-y-3 pt-1">
                {[
                  'Controle de limite total e limite disponível por cartão',
                  'Indicação automática do melhor dia de compra',
                  'Faturas organizadas com aviso de fechamento e vencimento',
                  'Parcelamento inteligente com projeção dos próximos meses',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-xs sm:text-sm text-slate-700 font-semibold">
                    <span className="w-5 h-5 rounded-full bg-emerald-200 text-emerald-800 flex items-center justify-center flex-shrink-0">
                      <Check size={13} strokeWidth={3} />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="pt-2">
                <button onClick={() => go('contato')} className="inline-flex items-center gap-2 rounded-full bg-[#0B2720] hover:bg-emerald-800 text-white font-extrabold px-7 py-3.5 text-sm transition-all cursor-pointer">
                  Organizar meus cartões <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────── DOBRA 4: PARA VOCÊ E PARA A SUA EMPRESA (PF / PJ) ────────────────── */}
      <section id="para-quem" className="py-16 sm:py-24 bg-white border-t border-slate-100 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <StatusBadge
              status="success"
              leftIcon={RiUserStarLine}
              rightIcon={RiBuilding4Line}
              leftLabel="Pessoa Física"
              rightLabel="Empresas & Negócios"
              className="mb-3 bg-emerald-50/70 border-emerald-200/80"
            />
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-[#0B2720]">
              Feito para você e para a sua empresa
            </h2>
            <p className="mt-3 text-slate-500 text-sm sm:text-base">
              O mesmo sistema inteligente, com o que cada perfil precisa. Você escolhe o que aparece no seu painel.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Bloco PF */}
            <div className="rounded-[36px] bg-[#FAFBF9] p-7 sm:p-9 border border-slate-200/80 shadow-xs flex flex-col justify-between">
              <div>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold mb-4">
                  <User size={13} /> Para você
                </span>
                <h3 className="text-2xl font-black text-[#0B2720]">Organize sua vida financeira pessoal</h3>
                <p className="text-xs sm:text-sm text-slate-500 mt-1.5 leading-relaxed">
                  Tenha mais controle, elimine dívidas e construa uma rotina financeira saudável e tranquila.
                </p>

                {/* Área visual com foto e cards flutuantes */}
                <div className="relative my-7 rounded-2xl bg-white p-4 border border-slate-100 shadow-xs flex items-center justify-between gap-4">
                  <div className="relative w-28 h-28 sm:w-32 sm:h-32 rounded-2xl overflow-hidden flex-shrink-0 shadow-xs ring-1 ring-slate-100">
                    <img
                      src="/images/pessoa-fisica.jpg"
                      alt="Pessoa Física"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center justify-between bg-emerald-50/80 border border-emerald-100 rounded-xl px-3 py-1.5 text-xs">
                      <span className="font-bold text-emerald-800">Salário</span>
                      <span className="font-extrabold text-emerald-600">+ R$ 2.500,00</span>
                    </div>
                    <div className="flex items-center justify-between bg-rose-50/80 border border-rose-100 rounded-xl px-3 py-1.5 text-xs">
                      <span className="font-bold text-rose-800">Mercado</span>
                      <span className="font-extrabold text-rose-500">- R$ 85,00</span>
                    </div>
                    <div className="flex items-center justify-between bg-slate-100 border border-slate-200 rounded-xl px-3 py-1.5 text-xs">
                      <span className="font-bold text-slate-700">Uber</span>
                      <span className="font-extrabold text-slate-800">- R$ 24,90</span>
                    </div>
                  </div>
                </div>

                <ul className="space-y-3">
                  {[
                    'Saldo, receitas, despesas e cartões na tela inicial',
                    'Fatura e limite de cada cartão em tempo real',
                    'Metas de economia e calendário de vencimentos',
                    'Lance gastos apenas conversando com o assistente',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-600 font-medium">
                      <Check size={17} className="text-emerald-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="pt-7">
                <button onClick={() => go('contato')} className="inline-flex items-center gap-2 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-extrabold px-6 py-3 text-xs sm:text-sm transition-all cursor-pointer">
                  Quero usar agora <ArrowRight size={15} />
                </button>
              </div>
            </div>

            {/* Bloco PJ */}
            <div className="rounded-[36px] bg-[#FAFBF9] p-7 sm:p-9 border border-slate-200/80 shadow-xs flex flex-col justify-between">
              <div>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal-100 text-teal-800 text-xs font-bold mb-4">
                  <Building2 size={13} /> Para a sua empresa
                </span>
                <h3 className="text-2xl font-black text-[#0B2720]">Gestão financeira mais simples</h3>
                <p className="text-xs sm:text-sm text-slate-500 mt-1.5 leading-relaxed">
                  Controle o caixa, acompanhe recebimentos e tome decisões melhores para o seu negócio.
                </p>

                {/* Área visual PJ */}
                <div className="relative my-7 rounded-2xl bg-white p-4 border border-slate-100 shadow-xs flex items-center justify-between gap-4">
                  <div className="relative w-28 h-28 sm:w-32 sm:h-32 rounded-2xl overflow-hidden flex-shrink-0 shadow-xs ring-1 ring-slate-100">
                    <img
                      src="/images/pessoa-juridica.jpg"
                      alt="Pessoa Jurídica"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center justify-between bg-emerald-50/80 border border-emerald-100 rounded-xl px-3 py-2 text-xs">
                      <span className="font-bold text-emerald-800">Recebimento</span>
                      <span className="font-extrabold text-emerald-600">+ R$ 8.500,00</span>
                    </div>
                    <div className="flex items-center justify-between bg-rose-50/80 border border-rose-100 rounded-xl px-3 py-2 text-xs">
                      <span className="font-bold text-rose-800">Fornecedor</span>
                      <span className="font-extrabold text-rose-500">- R$ 1.260,00</span>
                    </div>
                  </div>
                </div>

                <ul className="space-y-3">
                  {[
                    'Faturamento, lucro, margem e EBITDA calculados',
                    'Custos e despesas fixas, variáveis e não operacionais',
                    'Fluxo de caixa e contas a receber com inadimplência',
                    'Importação de planilhas e exportação para a contabilidade',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-600 font-medium">
                      <Check size={17} className="text-emerald-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="pt-7">
                <button onClick={() => go('contato')} className="inline-flex items-center gap-2 rounded-full bg-[#0B2720] hover:bg-emerald-800 text-white font-extrabold px-6 py-3 text-xs sm:text-sm transition-all cursor-pointer">
                  Conhecer planos <ArrowRight size={15} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────── DOBRA 5: ASSISTENTE FINANCEIRO COM IA ────────────────── */}
      <section id="assistente-ia" className="py-16 sm:py-24 bg-[#F2F7F4] border-t border-slate-200/60 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Lado Esquerdo: Mensagem */}
            <div className="space-y-6">
              <StatusBadge
                status="success"
                leftIcon={RiRobot2Line}
                leftLabel="Assistente IA Nativo"
                rightLabel="Conversação & Lançamento Inteligente"
                className="bg-emerald-100/70 border-emerald-300/80"
              />

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#0B2720] leading-tight">
                Lance falando do jeito que <span className="text-emerald-600">você fala</span>
              </h2>

              <p className="text-slate-600 leading-relaxed text-base">
                Escreva ou fale como se estivesse conversando com um amigo. O assistente organiza seus lançamentos, categoriza e mantém tudo atualizado automaticamente.
              </p>

              {/* 3 Caixas de diferenciais */}
              <div className="space-y-3.5 pt-1">
                {[
                  [MessageSquare, 'Entende sua linguagem', 'Fale naturalmente, sem termos técnicos ou regras chatas.'],
                  [Zap, 'Organiza automaticamente', 'Categorização inteligente, contas e parcelas calculadas com precisão.'],
                  [BarChart3, 'Dá insights personalizados', 'Receba análises e sugestões práticas para melhores decisões.'],
                ].map(([Icon, title, desc]) => (
                  <div key={title} className="flex items-start gap-3.5 bg-white border border-slate-200/70 rounded-2xl p-3.5 shadow-2xs">
                    <span className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon size={17} />
                    </span>
                    <div>
                      <p className="font-extrabold text-slate-900 text-sm">{title}</p>
                      <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Lado Direito: Chatbot com IA Interativo Gastify (Framer Motion) */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-tr from-emerald-500/20 via-teal-500/10 to-transparent rounded-[36px] blur-2xl -z-10 pointer-events-none" />
              <PhoneFrame decorative={false}>
                <PhoneChatScreen />
              </PhoneFrame>
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────── DOBRA 6: SHOWCASE "TODAS AS SUAS FINANÇAS EM UM SÓ APP" (novo design) ────────────────── */}
      <AppShowcase onStart={() => go('contato')} onDemo={() => go('assistente-ia')} />

      {/* ────────────────── DOBRA 7: SEGURANÇA E PRIVACIDADE ────────────────── */}
      <section id="seguranca" className="py-16 sm:py-24 bg-white scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <StatusBadge
              status="success"
              leftIcon={RiLockPasswordLine}
              rightIcon={RiShieldCheckLine}
              leftLabel="Segurança e Privacidade"
              rightLabel="Conexão protegida por HTTPS"
              className="mb-3 bg-emerald-50/70 border-emerald-200/80"
            />
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-[#0B2720]">
              A segurança dos seus dados é nossa prioridade
            </h2>
            <p className="mt-3 text-slate-500 text-sm sm:text-base leading-relaxed">
              Suas informações financeiras são sensíveis e tratamos isso com seriedade, utilizando as melhores práticas globais de segurança.
            </p>
          </div>

          <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-10 items-center">
            {/* Visual Ilustrativo do Escudo 3D */}
            <div className="relative rounded-[36px] bg-gradient-to-tr from-emerald-100/50 via-[#FAFBF9] to-teal-50 border border-slate-200/80 p-10 flex items-center justify-center shadow-xs">
              <div className="relative flex items-center justify-center">
                <div className="absolute w-44 h-44 rounded-full bg-emerald-400/20 blur-2xl" />
                <div className="relative w-32 h-32 rounded-3xl bg-[#0B2720] flex items-center justify-center text-emerald-400 shadow-xl">
                  <ShieldCheck size={64} strokeWidth={2.2} />
                </div>
                <div className="absolute -bottom-3 -right-3 w-12 h-12 rounded-2xl bg-emerald-500 text-slate-950 flex items-center justify-center shadow-md">
                  <Lock size={20} />
                </div>
              </div>
            </div>

            {/* Grid 2x2 com Garantias de Segurança */}
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                [Database, 'Dados separados', 'Cada cliente tem o seu próprio espaço de dados, isolado dos demais.'],
                [Shield, 'Conexão segura', 'A comunicação com o Gastify é protegida por HTTPS.'],
                [KeyRound, 'Acesso protegido', 'Proteção por senha forte com controle rigoroso contra acessos indevidos.'],
                [MonitorSmartphone, 'Você controla seus dispositivos', 'Gerencie quais dispositivos têm acesso à sua conta e desconecte com um clique.'],
              ].map(([Icon, title, desc]) => (
                <div key={title} className="rounded-2xl bg-[#FAFBF9] p-5 border border-slate-200/80 shadow-2xs hover:border-emerald-300 transition">
                  <span className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center mb-3">
                    <Icon size={19} />
                  </span>
                  <p className="font-extrabold text-[#0B2720] text-sm">{title}</p>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────── DOBRA 8: PERGUNTAS FREQUENTES (FAQ) ────────────────── */}
      <section id="perguntas" className="py-16 sm:py-24 bg-[#F2F7F4] border-t border-slate-200/60 scroll-mt-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <StatusBadge
              status="default"
              leftIcon={RiQuestionLine}
              leftLabel="FAQ"
              rightLabel="Dúvidas Frequentes"
              className="mb-3 bg-white/90 border-slate-200 shadow-2xs"
            />
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-[#0B2720]">
              Perguntas frequentes
            </h2>
            <p className="text-slate-500 text-sm mt-2">
              Tire suas dúvidas sobre o Gastify e comece a usar hoje mesmo.
            </p>
          </div>

          <div className="space-y-3">
            {FAQ.map(([q, a], i) => (
              <div key={q} className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-2xs">
                <button
                  onClick={() => setFaq(faq === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-slate-50 transition cursor-pointer"
                  aria-expanded={faq === i}
                >
                  <span className="font-extrabold text-[#0B2720] text-sm sm:text-base">{q}</span>
                  <ChevronDown size={18} className={`text-slate-400 flex-shrink-0 transition-transform duration-200 ${faq === i ? 'rotate-180 text-emerald-700' : ''}`} />
                </button>
                {faq === i && (
                  <div className="px-5 pb-5 pt-1 text-xs sm:text-sm text-slate-600 leading-relaxed border-t border-slate-100 bg-[#FAFBF9]">
                    {a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────────── DOBRA 9: FORMULÁRIO DE CONTATO (LeadForm) ────────────────── */}
      <section id="contato" className="py-16 sm:py-24 bg-white border-t border-slate-100 scroll-mt-20 text-slate-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 grid lg:grid-cols-[0.9fr_1.1fr] gap-10 items-center">
          <div className="space-y-6">
            <StatusBadge
              status="success"
              leftIcon={RiCheckboxCircleFill}
              leftLabel="Comece Agora"
              rightLabel="Acesso Exclusivo & Personalizado"
              className="bg-emerald-50/70 border-emerald-200/80"
            />

            <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-[#0B2720] leading-tight">
              Quero usar o Gastify
            </h2>

            <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
              Conte quem você é e como podemos falar com você. Entramos em contato para entender o que você precisa e liberar o seu acesso personalizado.
            </p>

            <ul className="space-y-3 text-xs sm:text-sm text-slate-700 font-semibold pt-2">
              {[
                'Resposta rápida pelo WhatsApp ou e-mail',
                'Atendimento sob medida para pessoa física e empresa',
                'Acesso liberado diretamente após o contato',
              ].map((i) => (
                <li key={i} className="flex items-center gap-2.5">
                  <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center flex-shrink-0">
                    <Check size={12} strokeWidth={3} />
                  </span>
                  <span>{i}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Componente Formulário */}
          <LeadForm />
        </div>
      </section>

      {/* ────────────────── RODAPÉ ────────────────── */}
      <footer className="bg-[#07241C] text-emerald-100/70 border-t border-emerald-900/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 grid sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr] gap-10">
          <div>
            <div className="flex items-center gap-2.5">
              <img src="/gastify-icon-192.png" alt="Gastify" className="w-9 h-9 rounded-xl shadow-xs" />
              <span className="text-xl font-black text-white tracking-tight">Gastify</span>
            </div>
            <p className="mt-4 text-xs sm:text-sm max-w-xs leading-relaxed text-emerald-200/60">
              Cada gasto, uma decisão consciente. Plataforma moderna de gestão financeira para pessoas e empresas.
            </p>
          </div>

          <div>
            <p className="text-sm font-black text-white mb-3">Navegação</p>
            <ul className="space-y-2 text-xs sm:text-sm">
              {nav.map(([l, id]) => (
                <li key={id}>
                  <button onClick={() => go(id)} className="hover:text-emerald-300 transition-colors cursor-pointer">
                    {l}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-sm font-black text-white mb-3">Acesso</p>
            <ul className="space-y-2 text-xs sm:text-sm">
              <li>
                <button onClick={() => setAccess(true)} className="hover:text-emerald-300 transition-colors cursor-pointer">
                  Acessar meu painel
                </button>
              </li>
              <li>
                <button onClick={() => go('contato')} className="hover:text-emerald-300 transition-colors cursor-pointer">
                  Quero usar
                </button>
              </li>
            </ul>
          </div>

          <div>
            <p className="text-sm font-black text-white mb-3">Legal</p>
            <ul className="space-y-2 text-xs sm:text-sm">
              {LEGAL_LINKS.map(([to, label]) => (
                <li key={to}>
                  <Link to={to} className="hover:text-emerald-300 transition-colors">
                    {label}
                  </Link>
                </li>
              ))}
              <li>
                <button onClick={() => window.dispatchEvent(new Event('gastify:cookie-open'))} className="hover:text-emerald-300 transition-colors cursor-pointer">
                  Preferências de cookies
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-emerald-900/50 py-5 text-center text-xs text-emerald-300/50">
          Gastify · feito por Akuant Solutions © 2026
        </div>
      </footer>

      {/* Modal e Cookies mantidos intactos */}
      <AccessModal open={access} onClose={() => setAccess(false)} />
      <CookieBanner />
    </div>
  );
}
